import React from 'react';
import { CalendarDays, Tractor, Map, TrendingUp, Percent, Clock, DollarSign, PiggyBank, AlertCircle, Globe } from 'lucide-react';
import ToolResult, { ResultItem } from '../../../../../components/ui/ToolResult';
import { formatNumber, formatCurrency } from '../../../../../utils/formatting';

/**
 * Компонент отображения результатов расчета сельскохозяйственных субсидий
 */
const ResultsOverview = ({ results, formData }) => {
  if (!results) return null;

  // Получение названий из идентификаторов
  const getFarmTypeName = () => {
    const farmTypes = {
      'crop': 'Растениеводство',
      'livestock': 'Животноводство',
      'mixed': 'Смешанное хозяйство',
      'poultry': 'Птицеводство',
      'dairy': 'Молочное животноводство',
      'organic': 'Органическое земледелие'
    };
    return farmTypes[formData.farmType] || 'Не указано';
  };

  const getRegionName = () => {
    const regions = {
      '77': 'Москва',
      '78': 'Санкт-Петербург',
      '50': 'Московская область',
      '23': 'Краснодарский край',
      '61': 'Ростовская область',
      '31': 'Белгородская область',
      '36': 'Воронежская область',
      '02': 'Республика Башкортостан',
      '16': 'Республика Татарстан',
      '74': 'Челябинская область',
      '55': 'Омская область',
      '24': 'Красноярский край',
      '38': 'Иркутская область',
      '25': 'Приморский край',
      '14': 'Республика Саха (Якутия)',
      '00': 'Другой регион'
    };
    return regions[formData.regionCode] || 'Не указано';
  };

  const getOrganizationTypeName = () => {
    const types = {
      'individual': 'КФХ / ИП',
      'legal': 'ООО / АО',
      'cooperative': 'Сельхозкооператив',
      'agro_holding': 'Агрохолдинг'
    };
    return types[formData.organizationType] || 'Не указано';
  };

  // Форматирование для отображения лет
  const formatYears = (years) => {
    if (years === 1) return '1 год';
    if (years > 1 && years < 5) return `${years} года`;
    return `${years} лет`;
  };

  return (
    <ToolResult title="Результаты расчета субсидий">
      {/* Ключевые метрики */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
        {/* Общая сумма субсидий */}
        <div className="rounded-lg bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-900/30 dark:to-teal-900/30 p-5 border border-emerald-100 dark:border-emerald-800/50 shadow-sm">
          <h3 className="text-base font-medium text-emerald-700 dark:text-emerald-300 mb-1.5 flex items-center gap-1.5">
            <PiggyBank className="w-4 h-4"/>
            Общая сумма субсидий
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            <span className="text-3xl font-bold text-emerald-800 dark:text-emerald-200" aria-live="polite">
              {formatCurrency(results.totalSubsidyAmount)}
            </span>
          </div>
          <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-1">
            На {results.programDuration} {formatYears(results.programDuration)}
          </p>
        </div>

        {/* Субсидия на гектар */}
        <div className="rounded-lg bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/30 dark:to-orange-900/30 p-5 border border-amber-100 dark:border-amber-800/50 shadow-sm">
          <h3 className="text-base font-medium text-amber-700 dark:text-amber-300 mb-1.5 flex items-center gap-1.5">
            <Tractor className="w-4 h-4"/>
            Субсидия на гектар
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            <span className="text-3xl font-bold text-amber-800 dark:text-amber-200" aria-live="polite">
              {formatCurrency(results.subsidyPerHectare)}
            </span>
            <span className="text-sm font-medium text-amber-600 dark:text-amber-400">
              /га
            </span>
          </div>
          <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
            Всего на {results.farmSize} га
          </p>
        </div>
      </div>

      {/* Детализированные результаты */}
      <div className="space-y-3 mb-6">
        <ResultItem 
          label="Тип хозяйства" 
          value={getFarmTypeName()} 
          color="emerald" 
          icon={<Tractor className="w-4 h-4"/>}
        />
        
        <ResultItem 
          label="Регион" 
          value={getRegionName()} 
          color="blue" 
          icon={<Map className="w-4 h-4"/>}
        />
        
        <ResultItem 
          label="Организационно-правовая форма" 
          value={getOrganizationTypeName()} 
          icon={<DollarSign className="w-4 h-4"/>} 
        />
        
        <ResultItem 
          label="Период программы" 
          value={`${results.programDuration} ${formatYears(results.programDuration)}`} 
          color="purple"
          icon={<Clock className="w-4 h-4"/>} 
        />

        <ResultItem 
          label="Дата начала программы" 
          value={results.startDate.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' })} 
          icon={<CalendarDays className="w-4 h-4"/>} 
        />
        
        <ResultItem 
          label="Дата окончания программы" 
          value={results.endDate.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' })} 
          icon={<CalendarDays className="w-4 h-4"/>} 
        />

        <div className="border-t border-gray-200 dark:border-gray-700 pt-3">
          <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">Детализация по видам субсидий:</h4>
          
          {results.capitalGrantAmount > 0 && (
            <ResultItem 
              label="Грант на капитальные затраты" 
              value={formatCurrency(results.capitalGrantAmount)} 
              color="blue" 
            />
          )}
          
          {results.operationalSubsidyAmount > 0 && (
            <ResultItem 
              label="Субсидия на операционные расходы" 
              value={formatCurrency(results.operationalSubsidyAmount)} 
              color="green" 
            />
          )}
          
          {results.loanSubsidyAmount > 0 && (
            <ResultItem 
              label="Субсидирование процентной ставки" 
              value={formatCurrency(results.loanSubsidyAmount)} 
              color="purple" 
            />
          )}
          
          {results.exportSupportAmount > 0 && (
            <ResultItem 
              label="Поддержка экспорта" 
              value={formatCurrency(results.exportSupportAmount)} 
              color="amber"
              icon={<Globe className="w-4 h-4"/>} 
            />
          )}

          <ResultItem 
            label="Общая сумма субсидий" 
            value={formatCurrency(results.totalSubsidyAmount)} 
            color="emerald" 
            large
          />
        </div>

        <div className="border-t border-gray-200 dark:border-gray-700 pt-3">
          <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">Применённые коэффициенты:</h4>
          
          <ResultItem 
            label="Коэффициент по размеру хозяйства" 
            value={formatNumber(results.baseSizeCoefficient, 2)} 
            color="teal" 
            icon={<Percent className="w-4 h-4"/>} 
          />
          
          <ResultItem 
            label="Коэффициент по опыту" 
            value={formatNumber(results.experienceCoefficient, 2)} 
            color="indigo" 
            icon={<Percent className="w-4 h-4"/>} 
          />
          
          <ResultItem 
            label="Коэффициент по дополнительным критериям" 
            value={formatNumber(results.additionalCriteriaCoefficient, 2)} 
            color="violet" 
            icon={<Percent className="w-4 h-4"/>} 
          />
          
          <ResultItem 
            label="Региональный коэффициент" 
            value={formatNumber(results.region.subsidyCoefficient, 2)} 
            color="blue" 
            icon={<Map className="w-4 h-4"/>} 
          />
        </div>
      </div>

      {/* Информация о возможных ограничениях */}
      <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 mt-2 border border-gray-200 dark:border-gray-700">
        <div className="flex items-start gap-3">
          <div className="mt-0.5">
            <AlertCircle className="w-5 h-5 text-amber-500" />
          </div>
          <div>
            <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-1">Важная информация по использованию расчета</h4>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Данный расчет является предварительным и может отличаться от фактически получаемого размера субсидии.
              Для получения точной информации обратитесь в региональный департамент сельского хозяйства или Министерство сельского хозяйства РФ.
            </p>
          </div>
        </div>
      </div>
    </ToolResult>
  );
};

export default ResultsOverview;